// Image enhancement before OCR. Works entirely client-side with the Canvas API.
// Steps: upscale small images -> grayscale -> contrast stretch -> sharpen
// (optional: Otsu black & white binarization for very faint images).

function clamp(v) {
  return Math.max(0, Math.min(255, Math.round(v)));
}

function loadImage(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Could not load image'));
    };
    img.src = url;
  });
}

function canvasToBlob(canvas) {
  return new Promise((resolve) => canvas.toBlob(resolve, 'image/png'));
}

// Otsu's method: pick the threshold that maximises between-class variance.
function otsuThreshold(data) {
  const hist = new Array(256).fill(0);
  const n = data.length / 4;
  for (let i = 0; i < data.length; i += 4) hist[data[i]]++;
  let sum = 0;
  for (let i = 0; i < 256; i++) sum += i * hist[i];
  let sumB = 0;
  let wB = 0;
  let maxVar = 0;
  let threshold = 127;
  for (let t = 0; t < 256; t++) {
    wB += hist[t];
    if (wB === 0) continue;
    const wF = n - wB;
    if (wF === 0) break;
    sumB += t * hist[t];
    const mB = sumB / wB;
    const mF = (sum - sumB) / wF;
    const between = wB * wF * (mB - mF) * (mB - mF);
    if (between > maxVar) {
      maxVar = between;
      threshold = t;
    }
  }
  return threshold;
}

// Unsharp mask sharpening on grayscale data.
function sharpenGray(data, width, height) {
  const amount = 0.7;
  const radius = 1;
  const out = new Uint8ClampedArray(data.length);
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const i = (y * width + x) * 4;
      let blur = 0;
      let count = 0;
      for (let dy = -radius; dy <= radius; dy++) {
        for (let dx = -radius; dx <= radius; dx++) {
          const nx = x + dx;
          const ny = y + dy;
          if (nx >= 0 && nx < width && ny >= 0 && ny < height) {
            blur += data[(ny * width + nx) * 4];
            count++;
          }
        }
      }
      blur /= count;
      const orig = data[i];
      out[i] = out[i + 1] = out[i + 2] = clamp(orig + amount * (orig - blur));
      out[i + 3] = 255;
    }
  }
  return out;
}

/**
 * Pre-process an image file for better OCR.
 * @param {File} file - image file (or Blob)
 * @param {object} opts
 * @returns {Promise<{blob: Blob, dataUrl: string, width: number, height: number}>}
 */
export async function preprocessImageFile(file, opts = {}) {
  const {
    upscale = true,
    maxScale = 2,
    contrast = true,
    sharpen = true,
    binarize = false,
    maxDim = 2600,
  } = opts;

  const img = await loadImage(file);
  const w = img.naturalWidth || img.width;
  const h = img.naturalHeight || img.height;

  // Upscale small / downscale huge images
  let scale = 1;
  if (w > 0 && h > 0) {
    if (upscale && w < 1100 && maxScale > 1) {
      scale = Math.min(maxScale, 1100 / w);
    }
    if (Math.max(w, h) > maxDim) {
      scale = Math.min(scale, maxDim / Math.max(w, h));
    }
  }
  const cw = Math.max(1, Math.round(w * scale));
  const ch = Math.max(1, Math.round(h * scale));

  const canvas = document.createElement('canvas');
  canvas.width = cw;
  canvas.height = ch;
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(img, 0, 0, cw, ch);

  const imageData = ctx.getImageData(0, 0, cw, ch);
  const data = imageData.data;

  // 1. Grayscale (always needed for contrast / binarize)
  for (let i = 0; i < data.length; i += 4) {
    const r = data[i];
    const g = data[i + 1];
    const b = data[i + 2];
    const gray = 0.299 * r + 0.587 * g + 0.114 * b;
    data[i] = data[i + 1] = data[i + 2] = gray;
  }

  // 2. Percentile contrast stretch (1%..99%)
  if (contrast) {
    const grayPix = new Float64Array(data.length / 4);
    for (let i = 0, j = 0; i < data.length; i += 4, j++) grayPix[j] = data[i];
    grayPix.sort();
    const lo = grayPix[Math.floor(grayPix.length * 0.01)];
    const hi = grayPix[Math.floor(grayPix.length * 0.99)];
    const range = hi - lo || 1;
    for (let i = 0; i < data.length; i += 4) {
      data[i] = data[i + 1] = data[i + 2] = clamp(((data[i] - lo) / range) * 255);
    }
  }

  // 3. Sharpen
  if (sharpen) {
    const sharp = sharpenGray(data, cw, ch);
    for (let i = 0; i < data.length; i++) data[i] = sharp[i];
  }

  // 4. Optional black & white thresholding
  if (binarize) {
    const th = otsuThreshold(data);
    for (let i = 0; i < data.length; i += 4) {
      const v = data[i] < th ? 0 : 255;
      data[i] = data[i + 1] = data[i + 2] = v;
    }
  }

  ctx.putImageData(imageData, 0, 0);

  const blob = await canvasToBlob(canvas);
  return { blob, dataUrl: canvas.toDataURL('image/png'), width: cw, height: ch };
}